import React, { useState, useEffect } from 'react';
import {
  useGetTradeDashboardCountersQuery,
  useGetTradeDashboardChartsQuery,
  useGetTradeDashboardAnalyticsQuery,
} from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import BarChart from '../../../components/charts/BarChart';
import PieChart from '../../../components/charts/PieChart';
import AreaChart from '../../../components/charts/AreaChart';
import { useLanguage } from '../../../context/LanguageContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBoxOpen,
  faNewspaper,
  faLeaf,
  faClock,
  faCheckCircle,
  faBan,
  faChessKnight,
  faCalendarDay,
  faUsers,
  faFire,
  faEye,
  faMousePointer,
  faShareAlt,
  faArrowTrendUp,
  faArrowTrendDown,
  faTrophy,
  faCoins,
  faPercent,
  faGlobe,
} from '@fortawesome/free-solid-svg-icons';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatNumber(n: number): string {
  if (n >= 1_000_000_000) return `${parseFloat((n / 1_000_000_000).toFixed(1))}B`;
  if (n >= 1_000_000) return `${parseFloat((n / 1_000_000).toFixed(1))}M`;
  if (n >= 1_000) return `${parseFloat((n / 1_000).toFixed(1))}K`;
  return n.toLocaleString();
}

// Theme palette: shades of the base green + neutral grays
const PIE_COLORS = [
  'rgba(57, 106, 84, 0.85)',
  'rgba(57, 106, 84, 0.55)',
  'rgba(57, 106, 84, 0.35)',
  'rgba(33, 37, 41, 0.75)',
  'rgba(33, 37, 41, 0.50)',
  'rgba(100, 116, 139, 0.75)',
  'rgba(100, 116, 139, 0.50)',
  'rgba(148, 163, 184, 0.75)',
];

const COUNTRY_COLORS = [
  'rgba(57, 106, 84, 0.80)',
  'rgba(57, 106, 84, 0.65)',
  'rgba(57, 106, 84, 0.50)',
  'rgba(57, 106, 84, 0.38)',
  'rgba(33, 37, 41, 0.70)',
  'rgba(33, 37, 41, 0.55)',
  'rgba(100, 116, 139, 0.70)',
  'rgba(100, 116, 139, 0.55)',
  'rgba(148, 163, 184, 0.70)',
  'rgba(148, 163, 184, 0.55)',
];

const getStatusBadge = (status: string) => {
  const map: Record<string, string> = {
    Active: 'badge bg-success',
    Pending: 'badge bg-warning text-dark',
    Rejected: 'badge bg-danger',
  };
  return map[status] || 'badge bg-secondary text-dark';
};

interface StatCardProps {
  label: string;
  value: number | string;
  icon: any;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon }) => (
  <div className="col-6 col-md-4 col-xl-3 mb-5">
    <div className="card card-flush bg-base">
      <div className="card-body d-flex align-items-center justify-content-between py-5 px-6">
        <div className="d-flex flex-column">
          <span className="fs-2hx fw-bold text-white lh-1 ls-n2">
            {typeof value === 'number' ? formatNumber(value) : value}
          </span>
          <span className="text-white opacity-75 pt-2 fw-semibold fs-6">{label}</span>
        </div>
        <FontAwesomeIcon icon={icon} className="text-white opacity-25" style={{ fontSize: 40 }} />
      </div>
    </div>
  </div>
);

// Placeholder card shown while a section's data (charts/analytics) is still loading.
const SectionSkeleton: React.FC<{ height?: number }> = ({ height = 280 }) => (
  <div className="row g-5 g-xl-10 mb-5">
    <div className="col-12">
      <div className="card card-flush">
        <div className="card-body p-8">
          <div className="skeleton-shimmer rounded mb-5" style={{ height: 18, width: '28%' }} />
          <div className="skeleton-shimmer rounded" style={{ height }} />
        </div>
      </div>
    </div>
  </div>
);

type ActiveTab = 'today' | 'week' | 'month';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function getStartOfWeekMonday(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay(); // 0 = Sun
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function toDateStr(d: Date): string {
  return d.toISOString().split('T')[0];
}

const AdminTradeDashboard: React.FC = () => {
  // Three parallel requests: fast counters for the cards, plus the heavier
  // charts and analytics slices that each render behind their own skeletons.
  const { data: countersRes, isLoading: countersLoading, error: countersError } = useGetTradeDashboardCountersQuery(undefined);
  const { data: chartsRes, isLoading: chartsLoading } = useGetTradeDashboardChartsQuery(undefined);

  // Analytics is warmed in the background. While the server reports warming=true
  // it returns an empty placeholder, so we keep the skeleton and poll until the
  // real data lands, then stop polling.
  const [analyticsPoll, setAnalyticsPoll] = useState(0);
  const { data: analyticsRes, isLoading: analyticsLoading } = useGetTradeDashboardAnalyticsQuery(undefined, {
    pollingInterval: analyticsPoll,
  });
  const analyticsWarming = !!(analyticsRes as any)?.warming;
  const analyticsPending = analyticsLoading || analyticsWarming;
  useEffect(() => {
    setAnalyticsPoll(analyticsWarming ? 5000 : 0);
  }, [analyticsWarming]);

  const { t, lang } = useLanguage();
  const [activeTab, setActiveTab] = useState<ActiveTab>('today');

  // Only block the first paint on the fast counters; the rest streams in.
  if (countersLoading) return <PageLoader />;

  if (countersError) {
    return (
      <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
        <div className="alert alert-danger m-5">{t('Error loading dashboard')}</div>
      </div>
    );
  }

  const d: any = {
    ...(countersRes?.data || {}),
    ...(chartsRes?.data || {}),
    ...(analyticsRes?.data || {}),
  };
  const au = d.active_users || {};
  const dauTrend: Array<{ date: string; count: number }> = au.dau_trend || [];

  // Derive chart labels + values per tab
  const chartData = (() => {
    if (activeTab === 'week') {
      const monday = getStartOfWeekMonday(new Date());
      const weekDates = Array.from({ length: 7 }, (_, i) => {
        const d = new Date(monday);
        d.setDate(monday.getDate() + i);
        return toDateStr(d);
      });
      return {
        labels: DAY_LABELS,
        values: weekDates.map(date => dauTrend.find(t => t.date === date)?.count ?? 0),
      };
    }
    // today + month: full month trend
    return {
      labels: dauTrend.map(t => String(new Date(t.date).getUTCDate())),
      values: dauTrend.map(t => t.count),
    };
  })();

  const tabMetric = {
    today: { value: au.dau ?? 0, change: au.dau_change ?? null, label: t('Daily Active Users'), vsLabel: t('vs yesterday') },
    week:  { value: au.wau ?? 0, change: au.wau_change ?? null, label: t('Weekly Active Users'), vsLabel: t('vs last week') },
    month: { value: au.mau ?? 0, change: au.mau_change ?? null, label: t('Monthly Active Users'), vsLabel: t('vs last month') },
  }[activeTab];

  const avgCtr: number = d.avg_ctr || 0;

  const stats: Array<{ label: string; value: number | string; icon: any }> = [
    { label: t('Total Ads'), value: d.total_ads || 0, icon: faNewspaper },
    { label: t('Total Users'), value: d.total_users || 0, icon: faUsers },
    { label: t('Active Ads'), value: d.active_ads || 0, icon: faCheckCircle },
    { label: t('Total Packages'), value: d.total_packages || 0, icon: faBoxOpen },
    { label: t('Pending Ads'), value: d.pending_ads || 0, icon: faClock },
    { label: t('Rejected Ads'), value: d.rejected_ads || 0, icon: faBan },
    { label: t('Free Ads'), value: d.free_ads || 0, icon: faLeaf },
    { label: t('Strategic Ads'), value: d.strategic_ads || 0, icon: faChessKnight },
    { label: t("Today's Ads"), value: d.todays_ads || 0, icon: faCalendarDay },
    { label: t('Completed Ads'), value: d.completed_ads || 0, icon: faTrophy },
    { label: t('Ad Revenue'), value: `$${formatNumber(d.advertising_revenue || 0)}`, icon: faCoins },
    { label: t('Avg. CTR'), value: `${avgCtr.toFixed(2)}%`, icon: faPercent },
    { label: t('Active Countries'), value: d.active_countries || 0, icon: faGlobe },
  ];

  const countryData: Array<{ country: string; country_ar?: string; total: number; percentage: number }> = d.country_wise_customers || [];
  const cityData: Array<{ city: string; city_ar?: string; country: string; country_ar?: string; total: number; percentage: number }> = d.city_wise_customers || [];

  const catData = (d.top_categories_by_ads || []).slice(0, 5);
  const catLabels = catData.map((c: any) => c.category);
  const catValues = catData.map((c: any) => c.total);

  const monthlyAds = d.monthly_ads || Array(12).fill(0);
  const trendingAds = d.top_trending_ads || [];

  // User Behaviour Analytics
  const peakHours: number[] = d.peak_hours || Array(24).fill(0);
  const hourLabels = Array.from({ length: 24 }, (_, i) => {
    if (i === 0) return '12a';
    if (i === 12) return '12p';
    return i < 12 ? `${i}a` : `${i - 12}p`;
  });
  const peakMax = Math.max(...peakHours, 1);
  const peakHourIdx = peakHours.indexOf(peakMax);

  const sd = d.session_duration || {};
  const totalSessions: number = sd.total_sessions || 0;
  const sdBuckets = [
    { label: t('Under 1 min'), value: sd.under_1_min || 0, color: '#396A54' },
    { label: t('1–5 minutes'), value: sd.one_to_5_min || 0, color: '#64748b' },
    { label: t('Over 10 min'), value: sd.over_10_min || 0, color: '#94a3b8' },
  ];

  const ret = d.retention || {};

  // Account Type Breakdown
  const atb = d.account_type_breakdown || {};
  const atbTypes: Array<{ label: string; label_ar?: string; count: number; percentage: number }> = atb.types || [];
  const atbColors = ['rgba(57,106,84,0.85)', 'rgba(33,37,41,0.70)', 'rgba(148,163,184,0.75)'];

  // Most Active Sections & Pages
  const mostVisitedPages: Array<{
    page_name: string;
    visits: number;
    percentage: number;
    avg_time_seconds: number;
    avg_time_formatted: string;
  }> = d.most_visited_pages || [];

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">

        {/* Toolbar */}
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                {t('Trade Dashboard')}
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <span className="text-muted text-hover-primary">{t('Home')}</span>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">{t('Trade Dashboard')}</li>
              </ul>
            </div>
          </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-fluid">

            {/* Stat Cards */}
            <div className="row g-5 g-xl-10 mb-5">
              {stats.map((s, i) => (
                <StatCard key={i} {...s} />
              ))}
            </div>

            {/* ── Active Users Metrics ── */}
            {analyticsPending ? <SectionSkeleton height={420} /> : (
            <div className="row g-5 g-xl-10 mb-5">
              <div className="col-12">
                <div className="card card-flush">
                  <div className="card-header pt-5 pb-0 border-bottom-0">
                    <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between w-100 gap-3">
                      <div>
                        <h3 className="card-label fw-bold text-dark mb-1">{t('Active Users')}</h3>
                        <span className="text-gray-400 fw-semibold fs-6">{t('Unique users who opened the app')}</span>
                      </div>
                      {/* Online now badge */}
                      <div className="d-flex align-items-center gap-2 px-4 py-2 rounded"
                        style={{ background: 'rgba(57,106,84,0.08)', border: '1px solid rgba(57,106,84,0.18)' }}>
                        <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#22c55e', display: 'inline-block', flexShrink: 0 }} />
                        <span className="fw-bold text-dark fs-6">{(au.online_now ?? 0).toLocaleString()}</span>
                        <span className="text-muted fw-semibold fs-7">{t('online now')}</span>
                      </div>
                    </div>

                    {/* Filter tabs */}
                    <div className="d-flex mt-4">
                      {(['today', 'week', 'month'] as ActiveTab[]).map((tab) => {
                        const labels: Record<ActiveTab, string> = { today: t('Today'), week: t('This Week'), month: t('This Month') };
                        return (
                          <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className="btn btn-sm me-1 fw-semibold fs-7"
                            style={{
                              borderRadius: 6,
                              padding: '5px 14px',
                              background: activeTab === tab ? '#396A54' : 'transparent',
                              color: activeTab === tab ? '#fff' : '#a1a5b7',
                              border: activeTab === tab ? 'none' : '1px solid #e4e6ef',
                              transition: 'all 0.15s',
                            }}
                          >
                            {labels[tab]}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="card-body pt-4">
                    {/* 4 equal metric cards */}
                    <div className="row g-4 mb-6">
                      {[
                        { label: t('Online Now'), sublabel: t('Currently active'), value: au.online_now ?? 0, change: null as number | null, live: true },
                        { label: t('DAU'), sublabel: t('Today vs yesterday'), value: au.dau ?? 0, change: au.dau_change ?? null, live: false },
                        { label: t('WAU'), sublabel: t('This week vs last'), value: au.wau ?? 0, change: au.wau_change ?? null, live: false },
                        { label: t('MAU'), sublabel: t('This month vs last'), value: au.mau ?? 0, change: au.mau_change ?? null, live: false },
                      ].map((m) => (
                        <div key={m.label} className="col-6 col-xl-3">
                          <div className="border rounded p-5 h-100 d-flex flex-column gap-3"
                            style={{ background: '#fafafa' }}>
                            <div className="d-flex align-items-center justify-content-between">
                              <span className="fw-semibold text-muted fs-7 d-flex align-items-center gap-2">
                                {m.live && (
                                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', display: 'inline-block', flexShrink: 0 }} />
                                )}
                                {m.label}
                              </span>
                              {m.change !== null && (
                                <span
                                  className="badge fw-semibold fs-8 d-flex align-items-center gap-1"
                                  style={{
                                    background: m.change >= 0 ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
                                    color: m.change >= 0 ? '#16a34a' : '#dc2626',
                                  }}
                                >
                                  <FontAwesomeIcon icon={m.change >= 0 ? faArrowTrendUp : faArrowTrendDown} />
                                  {m.change >= 0 ? '+' : ''}{m.change}%
                                </span>
                              )}
                            </div>
                            <span className="fw-bold text-dark" style={{ fontSize: 32, lineHeight: 1 }}>
                              {formatNumber(m.value)}
                            </span>
                            <span className="text-muted fs-8">{m.sublabel}</span>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Trend chart */}
                    <div style={{ minHeight: 200 }}>
                      {chartData.labels.length > 0 ? (
                        <AreaChart
                          labels={chartData.labels}
                          datasets={[
                            {
                              label: tabMetric.label,
                              data: chartData.values,
                              backgroundColor: 'rgba(57,106,84,0.12)',
                              borderColor: 'rgba(57,106,84,1)',
                            },
                          ]}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: { legend: { display: false } },
                            scales: {
                              y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.04)' } },
                              x: { grid: { display: false } },
                            },
                          }}
                        />
                      ) : (
                        <div className="d-flex align-items-center justify-content-center h-100">
                          <span className="text-muted fs-6">{t('No trend data yet')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* ── User Behaviour Analytics ── */}
            {analyticsPending ? <SectionSkeleton height={280} /> : (
            <div className="row g-5 g-xl-10 mb-5">

              {/* Peak Activity Hours */}
              <div className="col-12 col-xl-8">
                <div className="card card-flush h-100">
                  <div className="card-header pt-5">
                    <h3 className="card-title align-items-start flex-column">
                      <span className="card-label fw-bold text-dark">{t('Peak Activity Hours')}</span>
                      <span className="text-gray-400 mt-1 fw-semibold fs-6">
                        {t('Unique users per hour of day')} &nbsp;·&nbsp;
                        <span className="text-dark fw-bold">{hourLabels[peakHourIdx]}</span> {t('is peak')}
                      </span>
                    </h3>
                  </div>
                  <div className="card-body d-flex align-items-end p-0" style={{ minHeight: 240 }}>
                    <BarChart
                      labels={hourLabels}
                      datasets={[{
                        label: t('Users'),
                        data: peakHours,
                        backgroundColor: peakHours.map((_, i) =>
                          i === peakHourIdx ? 'rgba(57,106,84,1)' : 'rgba(57,106,84,0.35)'
                        ),
                        borderColor: 'transparent',
                        barThickness: 14,
                      }]}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { display: false } },
                        scales: {
                          y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.04)' } },
                          x: { grid: { display: false }, ticks: { font: { size: 10 } } },
                        },
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Retention Rate */}
              <div className="col-12 col-xl-4">
                <div className="card card-flush h-100">
                  <div className="card-header pt-5">
                    <h3 className="card-title align-items-start flex-column">
                      <span className="card-label fw-bold text-dark">{t('Retention Rate')}</span>
                      <span className="text-gray-400 mt-1 fw-semibold fs-6">{t('Users who returned this month')}</span>
                    </h3>
                  </div>
                  <div className="card-body d-flex flex-column justify-content-center align-items-center gap-4 py-6">
                    {/* Big % circle */}
                    <div className="d-flex flex-column align-items-center gap-2">
                      <div
                        className="d-flex align-items-center justify-content-center fw-bold text-white"
                        style={{
                          width: 120, height: 120, borderRadius: '50%',
                          background: 'linear-gradient(135deg, #396A54 0%, #2d5442 100%)',
                          fontSize: 30, lineHeight: 1,
                        }}
                      >
                        {ret.this_month ?? 0}%
                      </div>
                      <span className="text-muted fw-semibold fs-7">{t('this month')}</span>
                    </div>

                    {/* MoM comparison */}
                    <div className="d-flex align-items-center gap-3">
                      {ret.change !== null && ret.change !== undefined ? (
                        <>
                          <span
                            className="badge fw-semibold fs-7 d-flex align-items-center gap-1 px-3 py-2"
                            style={{
                              background: ret.change >= 0 ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
                              color: ret.change >= 0 ? '#16a34a' : '#dc2626',
                            }}
                          >
                            <FontAwesomeIcon icon={ret.change >= 0 ? faArrowTrendUp : faArrowTrendDown} />
                            {ret.change >= 0 ? '+' : ''}{ret.change}pp
                          </span>
                          <span className="text-muted fs-7">{t('vs last month')} ({ret.prev_month ?? 0}%)</span>
                        </>
                      ) : (
                        <span className="text-muted fs-7">{t('No previous month data')}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* Session Duration */}
            {analyticsPending ? <SectionSkeleton height={220} /> : (
            <div className="row g-5 g-xl-10 mb-5">
              <div className="col-12">
                <div className="card card-flush">
                  <div className="card-header pt-5">
                    <h3 className="card-title align-items-start flex-column">
                      <span className="card-label fw-bold text-dark">{t('Session Duration')}</span>
                      <span className="text-gray-400 mt-1 fw-semibold fs-6">
                        {t('Average time per visit')} &nbsp;·&nbsp; {totalSessions.toLocaleString()} {t('total sessions')}
                      </span>
                    </h3>
                  </div>
                  <div className="card-body py-6">
                    <div className="row g-5 align-items-center">

                      {/* Avg duration big number */}
                      <div className="col-12 col-md-3 d-flex flex-column align-items-center justify-content-center border-end">
                        <span className="text-muted fw-semibold fs-7 mb-2">{t('Avg. Session')}</span>
                        <span className="fw-bold text-dark" style={{ fontSize: 40, lineHeight: 1 }}>
                          {sd.avg_formatted || '0m 0s'}
                        </span>
                        <span className="text-muted fs-8 mt-1">{t('per visit')}</span>
                      </div>

                      {/* Distribution bars */}
                      <div className="col-12 col-md-9">
                        <div className="d-flex flex-column gap-5">
                          {sdBuckets.map((b) => {
                            const pct = totalSessions > 0 ? Math.round((b.value / totalSessions) * 100) : 0;
                            return (
                              <div key={b.label}>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <span className="fw-semibold fs-7 text-dark">{b.label}</span>
                                  <div className="d-flex align-items-center gap-3">
                                    <span className="fw-bold text-dark fs-7">{b.value.toLocaleString()}</span>
                                    <span className="badge fw-semibold fs-8"
                                      style={{ background: 'rgba(57,106,84,0.1)', color: '#396A54', minWidth: 42 }}>
                                      {pct}%
                                    </span>
                                  </div>
                                </div>
                                <div className="w-100 rounded" style={{ height: 8, background: '#f1f3f5', overflow: 'hidden' }}>
                                  <div
                                    className="rounded"
                                    style={{
                                      height: '100%',
                                      width: `${pct}%`,
                                      background: b.color,
                                      transition: 'width 0.6s ease',
                                      minWidth: pct > 0 ? 6 : 0,
                                    }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* ── Account Type Breakdown ── */}
            {analyticsPending ? <SectionSkeleton height={300} /> : (
            <div className="row g-5 g-xl-10 mb-5">
              <div className="col-12">
                <div className="card card-flush">
                  <div className="card-header pt-5">
                    <h3 className="card-title align-items-start flex-column">
                      <span className="card-label fw-bold text-dark">{t('Account Type Breakdown')}</span>
                      <span className="text-gray-400 mt-1 fw-semibold fs-6">
                        {t('Distribution of registered users by account type')} &nbsp;·&nbsp;
                        <span className="text-dark fw-bold">{(atb.total || 0).toLocaleString()}</span> {t('total')}
                      </span>
                    </h3>
                  </div>
                  <div className="card-body py-4">
                    <div className="row g-5 align-items-center">

                      {/* Table */}
                      <div className="col-12 col-xl-7">
                        <table className="table table-row-dashed table-row-gray-200 align-middle gs-0 gy-4 mb-0">
                          <thead>
                            <tr className="border-0">
                              <th className="p-0 fw-bold text-muted fs-7 text-uppercase">{t('Account Type')}</th>
                              <th className="p-0 fw-bold text-muted fs-7 text-uppercase text-end" style={{ width: 90 }}>{t('Count')}</th>
                              <th className="p-0 fw-bold text-muted fs-7 text-uppercase text-end" style={{ width: 80 }}>%</th>
                              <th className="p-0" style={{ width: 200 }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {atbTypes.length > 0 ? atbTypes.map((t, i) => (
                              <tr key={t.label}>
                                <td>
                                  <div className="d-flex align-items-center gap-3">
                                    <span style={{
                                      width: 10, height: 10, borderRadius: '50%',
                                      background: atbColors[i % atbColors.length],
                                      display: 'inline-block', flexShrink: 0,
                                    }} />
                                    <span className="fw-semibold text-dark fs-7">{(lang === 'ar' ? t.label_ar : t.label) || t.label}</span>
                                  </div>
                                </td>
                                <td className="text-end">
                                  <span className="fw-bold text-dark fs-7">{t.count.toLocaleString()}</span>
                                </td>
                                <td className="text-end">
                                  <span className="badge fw-semibold fs-8"
                                    style={{ background: 'rgba(57,106,84,0.1)', color: '#396A54', minWidth: 46 }}>
                                    {t.percentage}%
                                  </span>
                                </td>
                                <td>
                                  <div className="w-100 rounded ms-2" style={{ height: 7, background: '#f1f3f5', overflow: 'hidden' }}>
                                    <div className="rounded" style={{
                                      height: '100%',
                                      width: `${t.percentage}%`,
                                      background: atbColors[i % atbColors.length],
                                      transition: 'width 0.6s ease',
                                      minWidth: t.percentage > 0 ? 4 : 0,
                                    }} />
                                  </div>
                                </td>
                              </tr>
                            )) : (
                              <tr>
                                <td colSpan={4} className="text-center text-muted py-6">{t('No data available')}</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* Donut chart */}
                      <div className="col-12 col-xl-5 d-flex justify-content-center">
                        {atbTypes.length > 0 && atbTypes.some(t => t.count > 0) ? (
                          <div style={{ width: '100%', maxWidth: 280, height: 240 }}>
                            <PieChart
                              labels={atbTypes.map(t => (lang === 'ar' ? t.label_ar : t.label) || t.label)}
                              data={atbTypes.map(t => t.count)}
                              backgroundColor={atbColors}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                cutout: '62%',
                                plugins: {
                                  legend: {
                                    display: true,
                                    position: 'bottom' as const,
                                    labels: { boxWidth: 12, padding: 14, font: { size: 11 }, usePointStyle: true },
                                  },
                                },
                              }}
                            />
                          </div>
                        ) : (
                          <span className="text-muted fs-7">{t('No data')}</span>
                        )}
                      </div>

                    </div>
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* Charts row: Monthly Ads + Categories */}
            {chartsLoading ? <SectionSkeleton height={300} /> : (
            <div className="row g-5 g-xl-10 mb-5">

              {/* Monthly Ads Bar Chart */}
              <div className="col-12 col-xl-7">
                <div className="card card-flush h-100">
                  <div className="card-header pt-5">
                    <h3 className="card-title align-items-start flex-column">
                      <span className="card-label fw-bold text-dark">{t('Monthly Ads')}</span>
                      <span className="text-gray-400 mt-1 fw-semibold fs-6">
                        {t('Ads published per month')} — {new Date().getFullYear()}
                      </span>
                    </h3>
                  </div>
                  <div className="card-body d-flex align-items-end p-0" style={{ minHeight: 300 }}>
                    <BarChart
                      labels={MONTH_LABELS}
                      datasets={[
                        {
                          label: t('Ads Published'),
                          data: monthlyAds,
                          backgroundColor: 'rgba(57, 106, 84, 0.75)',
                          borderColor: 'rgba(57, 106, 84, 1)',
                          barThickness: 28,
                        },
                      ]}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { display: false } },
                        scales: {
                          y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.04)' } },
                          x: { grid: { display: false } },
                        },
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Top Categories Doughnut Chart */}
              <div className="col-12 col-xl-5">
                <div className="card card-flush h-100">
                  <div className="card-header pt-5">
                    <h3 className="card-title align-items-start flex-column">
                      <span className="card-label fw-bold text-dark">{t('Top Categories by Ads')}</span>
                      <span className="text-gray-400 mt-1 fw-semibold fs-6">{t('Distribution of ads per category')}</span>
                    </h3>
                  </div>
                  <div
                    className="card-body d-flex align-items-center justify-content-center pt-0 pb-5"
                    style={{ minHeight: 280, position: 'relative' }}
                  >
                    {catLabels.length > 0 ? (
                      <div style={{ width: '100%', maxWidth: 320, height: 260 }}>
                        <PieChart
                          labels={catLabels}
                          data={catValues}
                          backgroundColor={PIE_COLORS}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                display: true,
                                position: 'bottom' as const,
                                labels: {
                                  boxWidth: 12,
                                  padding: 12,
                                  font: { size: 11 },
                                  usePointStyle: true,
                                },
                              },
                            },
                          }}
                        />
                      </div>
                    ) : (
                      <span className="text-muted">{t('No category data available')}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* ── Most Active Sections & Pages ── */}
            {analyticsPending ? <SectionSkeleton height={300} /> : (
            <div className="row g-5 g-xl-10 mb-5">
              <div className="col-12">
                <div className="card card-flush">
                  <div className="card-header pt-5">
                    <h3 className="card-title align-items-start flex-column">
                      <span className="card-label fw-bold text-dark">{t('Most Active Sections & Pages')}</span>
                      <span className="text-gray-400 mt-1 fw-semibold fs-6">{t('Ranked by visit volume with average time spent')}</span>
                    </h3>
                  </div>
                  <div className="card-body py-4">
                    {mostVisitedPages.length > 0 ? (
                      <table className="table table-row-dashed table-row-gray-200 align-middle gs-0 gy-3 mb-0">
                        <thead>
                          <tr className="border-0">
                            <th className="p-0 fw-bold text-muted fs-7 text-uppercase" style={{ width: 40 }}>#</th>
                            <th className="p-0 fw-bold text-muted fs-7 text-uppercase">{t('Section / Page')}</th>
                            <th className="p-0 fw-bold text-muted fs-7 text-uppercase" style={{ width: 220 }}>{t('% of Total Visits')}</th>
                            <th className="p-0 fw-bold text-muted fs-7 text-uppercase text-end" style={{ width: 120 }}>{t('Avg. Time Spent')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {mostVisitedPages.map((page, i) => (
                            <tr key={page.page_name}>
                              <td>
                                <span
                                  className="fw-bold fs-8"
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    width: 24, height: 24, borderRadius: 6,
                                    background: i < 3 ? '#396A54' : '#f1f3f5',
                                    color: i < 3 ? '#fff' : '#a1a5b7', fontSize: 11,
                                  }}
                                >
                                  {i + 1}
                                </span>
                              </td>
                              <td>
                                <span className="fw-semibold text-dark fs-7">{page.page_name}</span>
                                <span className="text-muted fw-semibold d-block fs-8">{page.visits.toLocaleString()} {t('visits')}</span>
                              </td>
                              <td>
                                <div className="d-flex align-items-center gap-3">
                                  <div className="flex-grow-1 rounded" style={{ height: 6, background: '#f1f3f5', overflow: 'hidden', minWidth: 80 }}>
                                    <div
                                      className="rounded"
                                      style={{
                                        height: '100%',
                                        width: `${page.percentage}%`,
                                        background: COUNTRY_COLORS[i % COUNTRY_COLORS.length],
                                        transition: 'width 0.6s ease',
                                        minWidth: page.percentage > 0 ? 4 : 0,
                                      }}
                                    />
                                  </div>
                                  <span
                                    className="badge fw-semibold fs-8"
                                    style={{ background: 'rgba(57,106,84,0.1)', color: '#396A54', minWidth: 44 }}
                                  >
                                    {page.percentage}%
                                  </span>
                                </div>
                              </td>
                              <td className="text-end">
                                <span className="fw-bold text-dark fs-7">{page.avg_time_formatted}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="d-flex align-items-center justify-content-center py-10">
                        <span className="text-muted fs-6">{t('No page visit data available yet')}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* ── Geographic Distribution ── */}
            {chartsLoading ? <SectionSkeleton height={320} /> : (
            <div className="row g-5 g-xl-10 mb-5">

              {/* Top 10 Countries */}
              <div className="col-12 col-xl-6">
                <div className="card card-flush h-100">
                  <div className="card-header pt-5">
                    <h3 className="card-title align-items-start flex-column">
                      <span className="card-label fw-bold text-dark">{t('Top 10 Countries')}</span>
                      <span className="text-gray-400 mt-1 fw-semibold fs-6">{t('Users by country of registration')}</span>
                    </h3>
                  </div>
                  <div className="card-body py-4">
                    {countryData.length > 0 ? (
                      <table className="table table-row-dashed table-row-gray-200 align-middle gs-0 gy-3 mb-0">
                        <thead>
                          <tr className="border-0">
                            <th className="p-0 fw-bold text-muted fs-7 text-uppercase">#</th>
                            <th className="p-0 fw-bold text-muted fs-7 text-uppercase">{t('Country')}</th>
                            <th className="p-0 fw-bold text-muted fs-7 text-uppercase text-end" style={{ width: 70 }}>{t('Users')}</th>
                            <th className="p-0 fw-bold text-muted fs-7 text-uppercase text-end" style={{ width: 60 }}>%</th>
                            <th className="p-0" style={{ width: 120 }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {countryData.map((c, i) => (
                            <tr key={c.country}>
                              <td>
                                <span className="fw-bold text-muted fs-8"
                                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    width: 22, height: 22, borderRadius: 6,
                                    background: i < 3 ? '#396A54' : '#f1f3f5',
                                    color: i < 3 ? '#fff' : '#a1a5b7', fontSize: 11 }}>
                                  {i + 1}
                                </span>
                              </td>
                              <td>
                                <span className="fw-semibold text-dark fs-7">{(lang === 'ar' ? c.country_ar : c.country) || c.country}</span>
                              </td>
                              <td className="text-end">
                                <span className="fw-bold text-dark fs-7">{c.total.toLocaleString()}</span>
                              </td>
                              <td className="text-end">
                                <span className="badge fw-semibold fs-8"
                                  style={{ background: 'rgba(57,106,84,0.1)', color: '#396A54', minWidth: 40 }}>
                                  {c.percentage}%
                                </span>
                              </td>
                              <td>
                                <div className="w-100 rounded ms-2" style={{ height: 6, background: '#f1f3f5', overflow: 'hidden' }}>
                                  <div className="rounded" style={{
                                    height: '100%', width: `${c.percentage}%`,
                                    background: COUNTRY_COLORS[i % COUNTRY_COLORS.length],
                                    transition: 'width 0.6s ease', minWidth: c.percentage > 0 ? 4 : 0,
                                  }} />
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="d-flex align-items-center justify-content-center py-10">
                        <span className="text-muted fs-6">{t('No country data available')}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Top 10 Cities */}
              <div className="col-12 col-xl-6">
                <div className="card card-flush h-100">
                  <div className="card-header pt-5">
                    <h3 className="card-title align-items-start flex-column">
                      <span className="card-label fw-bold text-dark">{t('Top 10 Cities')}</span>
                      <span className="text-gray-400 mt-1 fw-semibold fs-6">{t('Most active cities by user count')}</span>
                    </h3>
                  </div>
                  <div className="card-body py-4">
                    {cityData.length > 0 ? (
                      <table className="table table-row-dashed table-row-gray-200 align-middle gs-0 gy-3 mb-0">
                        <thead>
                          <tr className="border-0">
                            <th className="p-0 fw-bold text-muted fs-7 text-uppercase">#</th>
                            <th className="p-0 fw-bold text-muted fs-7 text-uppercase">{t('City')}</th>
                            <th className="p-0 fw-bold text-muted fs-7 text-uppercase">{t('Country')}</th>
                            <th className="p-0 fw-bold text-muted fs-7 text-uppercase text-end" style={{ width: 70 }}>{t('Users')}</th>
                            <th className="p-0 fw-bold text-muted fs-7 text-uppercase text-end" style={{ width: 60 }}>%</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cityData.map((c, i) => (
                            <tr key={`${c.city}-${c.country}`}>
                              <td>
                                <span className="fw-bold text-muted fs-8"
                                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    width: 22, height: 22, borderRadius: 6,
                                    background: i < 3 ? '#396A54' : '#f1f3f5',
                                    color: i < 3 ? '#fff' : '#a1a5b7', fontSize: 11 }}>
                                  {i + 1}
                                </span>
                              </td>
                              <td>
                                <span className="fw-semibold text-dark fs-7">{(lang === 'ar' ? c.city_ar : c.city) || c.city}</span>
                              </td>
                              <td>
                                <span className="text-muted fs-7">{(lang === 'ar' ? c.country_ar : c.country) || c.country}</span>
                              </td>
                              <td className="text-end">
                                <span className="fw-bold text-dark fs-7">{c.total.toLocaleString()}</span>
                              </td>
                              <td className="text-end">
                                <span className="badge fw-semibold fs-8"
                                  style={{ background: 'rgba(57,106,84,0.1)', color: '#396A54', minWidth: 40 }}>
                                  {c.percentage}%
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="d-flex align-items-center justify-content-center py-10">
                        <span className="text-muted fs-6">{t('No city data available')}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

            </div>
            )}

            {/* Top Trending Ads Table */}
            {chartsLoading ? <SectionSkeleton height={320} /> : (
            <div className="row g-5 g-xl-10 mb-10">
              <div className="col-12">
                <div className="card card-flush">
                  <div className="card-header pt-5">
                    <h3 className="card-title align-items-start flex-column">
                      <span className="card-label fw-bold text-dark d-flex align-items-center gap-2">
                        <FontAwesomeIcon icon={faFire} style={{ color: '#396A54' }} />
                        {t('Top Trending Ads')}
                      </span>
                      <span className="text-gray-400 mt-1 fw-semibold fs-6">{t('Highest performing ads by views')}</span>
                    </h3>
                  </div>
                  <div className="card-body pt-3">
                    {trendingAds.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-row-dashed table-row-gray-200 align-middle gs-0 gy-4">
                          <thead>
                            <tr className="border-0">
                              <th className="p-0 w-30px">#</th>
                              <th className="p-0 min-w-200px">{t('Ad Title')}</th>
                              <th className="p-0 min-w-120px">{t('Category')}</th>
                              <th className="p-0 min-w-120px">{t('Posted By')}</th>
                              <th className="p-0 min-w-80px">{t('Status')}</th>
                              <th className="p-0 min-w-80px text-center">
                                <FontAwesomeIcon icon={faEye} className="me-1 text-muted" />{t('Views')}
                              </th>
                              <th className="p-0 min-w-80px text-center">
                                <FontAwesomeIcon icon={faMousePointer} className="me-1 text-muted" />{t('Clicks')}
                              </th>
                              <th className="p-0 min-w-80px text-center">
                                <FontAwesomeIcon icon={faShareAlt} className="me-1 text-muted" />{t('Shares')}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {trendingAds.map((ad: any, i: number) => (
                              <tr key={ad.id}>
                                <td>
                                  <span
                                    className="d-flex align-items-center justify-content-center text-white fw-bold fs-8"
                                    style={{
                                      width: 26,
                                      height: 26,
                                      borderRadius: 6,
                                      backgroundColor: i === 0 ? '#f6c000' : i === 1 ? '#a1a5b7' : i === 2 ? '#a47248' : '#396A54',
                                    }}
                                  >
                                    {i + 1}
                                  </span>
                                </td>
                                <td>
                                  <span className="text-dark fw-semibold fs-7">{ad.title || '-'}</span>
                                </td>
                                <td>
                                  <span className="text-muted fw-semibold fs-7">{ad.category}</span>
                                </td>
                                <td>
                                  <span className="text-muted fw-semibold fs-7">{ad.user}</span>
                                </td>
                                <td>
                                  <span className={getStatusBadge(ad.status)} style={{ fontSize: 11 }}>
                                    {ad.status ? t(ad.status) : ad.status}
                                  </span>
                                </td>
                                <td className="text-center">
                                  <span className="fw-bold text-dark fs-7">{(ad.views || 0).toLocaleString()}</span>
                                </td>
                                <td className="text-center">
                                  <span className="fw-bold text-dark fs-7">{(ad.clicks || 0).toLocaleString()}</span>
                                </td>
                                <td className="text-center">
                                  <span className="fw-bold text-dark fs-7">{(ad.shares || 0).toLocaleString()}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="d-flex flex-column align-items-center justify-content-center py-10">
                        <FontAwesomeIcon icon={faFire} className="text-muted mb-3" style={{ fontSize: 36, opacity: 0.25 }} />
                        <span className="text-muted fs-6">{t('No trending ads data available yet')}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminTradeDashboard;
